import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { calculatorSchema, retirementSchema, generateBreadcrumbSchema } from '../schemas';

// Mapeamento de rotas para dados SEO (definido fora do componente para evitar recriações)
const SEO_ROUTE_MAP = {
  '/': {
    title: 'Calculadora de Juros Compostos | Simule seus Investimentos',
    description: 'Calculadora de juros compostos online e gratuita. Simule seus investimentos, compare cenários e descubra quanto tempo levará para atingir seus objetivos financeiros.',
    keywords: ['juros compostos', 'calculadora financeira', 'investimentos', 'simulação financeira', 'planejamento financeiro'],
    structuredData: calculatorSchema,
    image: '/images/calculator-social.jpg',
  },
  '/calculadora': {
    title: 'Calculadora de Juros Compostos | Simule seus Investimentos',
    description: 'Calculadora de juros compostos online e gratuita. Simule seus investimentos, compare cenários e descubra quanto tempo levará para atingir seus objetivos financeiros.',
    keywords: ['juros compostos', 'calculadora financeira', 'investimentos', 'simulação financeira', 'planejamento financeiro'],
    structuredData: calculatorSchema,
    image: '/images/calculator-social.jpg',
  },
  '/cenarios': {
    title: 'Compare Cenários de Investimento | Simulador de Juros Compostos',
    description: 'Compare diferentes cenários de investimento e descubra qual estratégia trará os melhores resultados ao longo do tempo.',
    keywords: ['comparação de investimentos', 'cenários financeiros', 'simulação de investimentos', 'juros compostos', 'estratégias de investimento'],
    image: '/images/scenarios-social.jpg',
  },
  '/marcos': {
    title: 'Defina Marcos Financeiros | Calculadora de Juros Compostos',
    description: 'Estabeleça objetivos financeiros e descubra quanto tempo levará para atingir cada um deles com nossa calculadora de marcos.',
    keywords: ['marcos financeiros', 'objetivos financeiros', 'planejamento financeiro', 'tempo de investimento', 'metas de investimento'],
    image: '/images/milestones-social.jpg',
  },
  '/aposentadoria': {
    title: 'Planejador de Aposentadoria | Calculadora de Independência Financeira',
    description: 'Descubra quanto você precisa investir para alcançar sua independência financeira e se aposentar com tranquilidade.',
    keywords: ['planejamento de aposentadoria', 'independência financeira', 'aposentadoria', 'renda passiva', 'liberdade financeira'],
    structuredData: retirementSchema,
    image: '/images/retirement-social.jpg',
  },
  '/guia': {
    title: 'Guia de Juros Compostos | Aprenda a Investir',
    description: 'Aprenda como funcionam os juros compostos e como utilizá-los para alcançar sua independência financeira. Guia completo com exemplos práticos.',
    keywords: ['guia juros compostos', 'como investir', 'educação financeira', 'independência financeira'],
    image: '/images/guide-social.jpg',
  },
  '/politica-de-privacidade': {
    title: 'Política de Privacidade | Calculadora de Juros Compostos',
    description: 'Nossa política de privacidade explica como coletamos, usamos e protegemos suas informações ao utilizar nossa calculadora de juros compostos.',
    keywords: ['política de privacidade', 'privacidade', 'dados pessoais', 'termos de uso'],
  },
  '/termos-de-uso': {
    title: 'Termos de Uso | Calculadora de Juros Compostos',
    description: 'Termos de uso da calculadora de juros compostos. Leia nossas diretrizes e regras para utilização da plataforma.',
    keywords: ['termos de uso', 'condições', 'política', 'regras de utilização'],
  },
};

// Criando o contexto
const SeoContext = createContext();

/**
 * Provedor de contexto para gerenciar metadados SEO em toda a aplicação
 * @param {Object} props - Propriedades do componente
 * @param {React.ReactNode} props.children - Componentes filhos
 */
export const SeoProvider = ({ children }) => {
  const location = useLocation();
  const isFirstRender = useRef(true);
  const lastPathRef = useRef(location.pathname);
  
  const [seoData, setSeoData] = useState({
    title: 'Calculadora de Juros Compostos | Simule seus Investimentos',
    description: 'Calculadora de juros compostos online e gratuita. Simule seus investimentos, compare cenários e descubra quanto tempo levará para atingir seus objetivos financeiros.',
    keywords: ['juros compostos', 'calculadora financeira', 'investimentos', 'simulação financeira', 'planejamento financeiro'],
    canonicalUrl: '',
    structuredData: calculatorSchema,
    image: '/images/calculator-social.jpg',
    type: 'website',
    locale: 'pt_BR'
  });

  // Atualiza os dados de SEO com base na rota atual
  useEffect(() => {
    try {
      const currentPath = location.pathname;
      
      // Verificar se o caminho mudou ou é a primeira renderização
      if (isFirstRender.current || currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath;
        isFirstRender.current = false;
        
        const currentSeoData = SEO_ROUTE_MAP[currentPath] || SEO_ROUTE_MAP['/'];
        const baseUrl = window.location.origin;
        const canonicalUrl = `${baseUrl}${currentPath}`;
        
        // Gerar breadcrumb schema para esta página
        const pageTitle = currentSeoData.title.split('|')[0].trim();
        const breadcrumbData = generateBreadcrumbSchema(pageTitle, canonicalUrl);
        
        // Combine o estruturado principal com breadcrumbs
        const combinedStructuredData = currentSeoData.structuredData 
          ? [currentSeoData.structuredData, breadcrumbData]
          : [breadcrumbData];

        setSeoData({
          ...currentSeoData,
          canonicalUrl,
          structuredData: combinedStructuredData
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar dados de SEO:', error);
    }
  }, [location.pathname]);

  // Função para atualizar manualmente os dados de SEO
  const updateSeoData = useCallback((newData) => {
    setSeoData(prevData => ({
      ...prevData,
      ...newData
    }));
  }, []);

  // Memoriza o valor do contexto para evitar renderizações desnecessárias
  const contextValue = useMemo(() => ({
    seoData,
    updateSeoData
  }), [seoData, updateSeoData]);

  return (
    <SeoContext.Provider value={contextValue}>
      {children}
    </SeoContext.Provider>
  );
};

// Hook personalizado para acessar o contexto
export const useSeo = () => {
  const context = useContext(SeoContext);
  if (!context) {
    throw new Error('useSeo deve ser usado dentro de um SeoProvider');
  }
  return context;
};

export default SeoContext;
